import React, { useEffect, useRef, useState } from "react";
import styles from "./PosUserEdit.module.css";
import Select from "react-select";
import { useHistory, Link, useParams } from "react-router-dom";
import CustomerSearchPopup from "../Customers/CustomerSearchPopup";
import { get, post, put } from "../../Common/api";
import { useDispatch } from "react-redux";
import Alert, {
  success as successNotificationType,
  getNotificationForError,
} from "../Alert";
import {
  setNextPageNotification,
  setCurrentPageNotification,
} from "../../redux/notificationSlice";
import { Input } from "@progress/kendo-react-inputs";
import { Error } from "@progress/kendo-react-labels";
import PasswordBox from "../../Common/PasswordBox";

export default function PosUserEdit() {
  const { id } = useParams();
  const isEdit = !isNaN(id);
  const dispatch = useDispatch();
  const [isMultiSelectionEnabled] = useState(false);
  const [customer, setCustomer] = useState({
    id: -1,
    name: "",
  });
  const [loginName, setLoginName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [posRoles, setPosRoles] = useState([]);
  const [visible, setVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState();
  const history = useHistory();
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const [customerValidationMessage, setCustomerValidationMessage] =
    useState("");
  const [roleValidationMessage, setRoleValidationMessage] = useState("");
  const [usernameValidationMessage, setUsernameValidationMessage] =
    useState("");
  const [passwordValidationMessage, setPasswordValidationMessage] =
    useState("");
  const [
    confirmPasswordValidationMessage,
    setConfirmPasswordValidationMessage,
  ] = useState("");
  const [errorFlashClassName, setErrorFlashClassName] = useState("");
  const [errorBorderFlashClassName, setErrorBorderFlashClassName] =
    useState("");
  const [isChangePasswordChecked, setIsChangePasswordChecked] = useState(false);
  const arePasswordFieldsDisabled = isEdit && !isChangePasswordChecked;

  const firstTimeValidation = useRef(true);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const onRoleSelectionChange = (selectedOption) => {
    setSelectedRole(selectedOption);
  };

  const customerSelectionCallback = (selectedCustomers) => {
    if (selectedCustomers && selectedCustomers.length > 0) {
      setCustomer({
        ...customer,
        id: selectedCustomers[0].id,
        name: `${selectedCustomers[0].lastName}, ${
          selectedCustomers[0].firstName
        } ${selectedCustomers[0].middle || ""}`,
      });
    }
  };

  const loadData = async () => {
    const allTasks = [ get("/posroles") ];
    if (isEdit) {
      allTasks.push(get(`/posusers/${id}`));
    }
    const results = await Promise.all(allTasks);
    return results;
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const [rolesData, userData] = await loadData();
        const options = rolesData.map((i) => {
          return {
            value: i.id,
            label: i.name,
          };
        });
        setPosRoles(options);
        if (userData) {
          const role = options.find(o => o.value === userData.posRoleId);
          setSelectedRole(role);
          setCustomer({
            id: userData.customerId,
            name: userData.name
          });
          setLoginName(userData.username);
        }
      } catch (error) {
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      }
    }
    fetchData();
  }, [dispatch]);

  const onSave = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    try {
      const posUser = {
        customerId: customer.id,
        username: loginName,
        password: arePasswordFieldsDisabled ? undefined : password,
        posRoleId: selectedRole.value,
      };
      if (isEdit) {
        await put(`/posusers/${id}`, posUser);
      } else {
        await post("/posusers", posUser);
      }
      dispatch(
        setNextPageNotification({
          type: successNotificationType,
          message: `POS user saved`,
        })
      );

      history.push("/posusers");
    } catch (errorObj) {
      if (errorObj && errorObj.data && errorObj.data.type === "validation") {
        const errors = errorObj.data.errors;
        if (errors) {
          resetErrors();
          if (errors["customer"]) {
            setCustomerValidationMessage(errors["customer"]);
          }
          if (errors["role"]) {
            setRoleValidationMessage(errors["role"]);
          }
          if (errors["username"]) {
            setUsernameValidationMessage(errors["username"]);
          }
          if (errors["password"]) {
            setPasswordValidationMessage(errors["password"]);
          }

          flashErrors();
          setHasFormErrors(true);
        }
      } else {
        dispatch(setCurrentPageNotification(getNotificationForError(errorObj)));
      }
    }
  };

  const validateForm = () => {
    let isValid = true;
    resetErrors();
    if (customer.name == null || customer.name === "") {
      setCustomerValidationMessage("Customer is required");
      isValid = false;
    }
    if (!selectedRole) {
      setRoleValidationMessage("POS Role is required");
      isValid = false;
    }
    if (!loginName) {
      setUsernameValidationMessage("User Name is required");
      isValid = false;
    }
    if (!arePasswordFieldsDisabled) {
      if (!password) {
        setPasswordValidationMessage("Password is required");
        isValid = false;
      }
      if (!confirmPassword) {
        setConfirmPasswordValidationMessage("Confirm Password is required");
        isValid = false;
      }
      if (password !== confirmPassword) {
        setConfirmPasswordValidationMessage("Password fields must match");
        isValid = false;
      }
    }

    setHasFormErrors(!isValid);
    flashErrors();
    return isValid;
  };

  const resetErrors = () => {
    setCustomerValidationMessage("");
    setRoleValidationMessage("");
    setUsernameValidationMessage("");
    setPasswordValidationMessage("");
    setConfirmPasswordValidationMessage("");
  };

  const flashErrors = () => {
    if (hasFormErrors && !firstTimeValidation.current) {
      setErrorFlashClassName(`${styles.flash_error_msg}`);
      setErrorBorderFlashClassName(`${styles.flash_error_border}`);
      setTimeout(() => {
        setErrorFlashClassName("");
        setErrorBorderFlashClassName("");
      }, 1000);
    }
    firstTimeValidation.current = false;
  };

  const clickCustomer = () => {
    if (!isEdit) {
      toggleDialog();
    }
  }

  return (
    <React.Fragment>
      <Alert />
      {visible && (
        <CustomerSearchPopup
          selectedCustomerIds={[customer.id]}
          toggleDialog={toggleDialog}
          customerSelectionCallback={customerSelectionCallback}
          isMultiSelectionEnabled={isMultiSelectionEnabled}
        />
      )}
      <div className="container">
        <form data-testid="form">
          <div className="row">
            {hasFormErrors && (
              <div className={"k-messagebox k-messagebox-error"}>
                {"One or more fields are invalid"}
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-md-6">
              <div>
                <label htmlFor="txtCustomer">
                  Customer<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div>
                <Input
                  type="text"
                  id="txtCustomer"
                  className={`${styles.textBoxName} ${
                    customerValidationMessage ? errorBorderFlashClassName : ""
                  } ${customerValidationMessage ? styles.error : ""} ${
                    isEdit ? styles.not_allowed : ""
                  }`}
                  readOnly={true}
                  placeholder="Customer"
                  value={customer.name}
                  required={true}
                  onClick={clickCustomer}
                ></Input>
                {customerValidationMessage && (
                  <Error className={`${errorFlashClassName}`}>
                    {customerValidationMessage}
                  </Error>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6" style={{ marginTop: "20px" }}>
              <div>
                <label htmlFor="ddlRole">
                  POS Role<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div>
                <Select
                  name="ddlRole"
                  inputId="ddlRole"
                  options={posRoles}
                  closeMenuOnSelect={true}
                  required={true}
                  onChange={onRoleSelectionChange}
                  value={selectedRole}
                  className={`${
                    roleValidationMessage ? errorBorderFlashClassName : ""
                  } ${roleValidationMessage ? styles.error : ""}`}
                ></Select>
                {roleValidationMessage && (
                  <Error className={`${errorFlashClassName}`}>
                    {roleValidationMessage}
                  </Error>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6" style={{ marginTop: "20px" }}>
              <div>
                <label htmlFor="txtUsername">
                  User Name<span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div>
                <Input
                  type="text"
                  id="txtUsername"
                  className={`${styles.textBoxName} ${
                    usernameValidationMessage ? errorBorderFlashClassName : ""
                  } ${usernameValidationMessage ? styles.error : ""}`}
                  placeholder="User Name"
                  value={loginName}
                  onChange={(event) => setLoginName(event.target.value)}
                  maxLength={15}
                ></Input>
                {usernameValidationMessage && (
                  <Error className={`${errorFlashClassName}`}>
                    {usernameValidationMessage}
                  </Error>
                )}
              </div>
            </div>
          </div>
          { isEdit 
            ? <div className="row">
                <div className="col-md-6" style={{ marginTop: "20px" }}>
                  <div>
                    <label htmlFor="chkChangePassword">
                      Change Password
                    </label>
                  </div>
                  <input
                    id="chkChangePassword"
                    type="checkbox"
                    checked={isChangePasswordChecked}
                    onChange={e => setIsChangePasswordChecked(!isChangePasswordChecked) }
                  ></input>
                </div>
              </div>
            : null }
          <div className="row">
            <div className="col-md-6" style={{ marginTop: "20px" }}>
              <div>
                <label htmlFor="txtPassword">
                  Password{ arePasswordFieldsDisabled ? null : <span style={{ color: "red" }}>*</span> }
                </label>
              </div>
              <PasswordBox
                id="txtPassword"
                placeholder="Password"
                password={password}
                setPassword={setPassword}
                className={`${
                  passwordValidationMessage ? errorBorderFlashClassName : ""
                } ${passwordValidationMessage ? styles.error : ""}`}
                disabled={arePasswordFieldsDisabled}
                maxLength={15}
              />
              {passwordValidationMessage && (
                <Error className={`${errorFlashClassName}`}>
                  {passwordValidationMessage}
                </Error>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6" style={{ marginTop: "20px" }}>
              <div>
                <label htmlFor="txtConfirmPassword">
                  Confirm Password{ arePasswordFieldsDisabled ? null : <span style={{ color: "red" }}>*</span> }
                </label>
              </div>
              <div>
                <PasswordBox
                  id="txtConfirmPassword"
                  placeholder="Confirm Password"
                  password={confirmPassword}
                  setPassword={setConfirmPassword}
                  isValid={password === confirmPassword}
                  dataTestId="test-confirm-password"
                  className={`${
                    confirmPasswordValidationMessage
                      ? errorBorderFlashClassName
                      : ""
                  } ${confirmPasswordValidationMessage ? styles.error : ""}`}
                  disabled={arePasswordFieldsDisabled}
                />
                {confirmPasswordValidationMessage && (
                  <Error className={`${errorFlashClassName}`}>
                    {confirmPasswordValidationMessage}
                  </Error>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div>
                <Link to="/posusers">
                  <input
                    type="button"
                    className="btn default btn-lg pull-right"
                    style={{ marginTop: "20px" }}
                    value="Cancel"
                  ></input>
                </Link>
                <input
                  type="button"
                  className="btn blue btn-lg pull-right"
                  style={{ margin: "20px 10px 0px 0px" }}
                  value="Save"
                  onClick={onSave}
                ></input>
              </div>
            </div>
          </div>
        </form>
      </div>
    </React.Fragment>
  );
}
